/**
 * Loads YouTube IFrame API and initializes .yt-clip elements (iframe or div with data-vid/start/end)
 */
export class YoutubeClipManager {
  private static scriptInjected = false;

  static injectScript(): void {
    if (YoutubeClipManager.scriptInjected) return;
    YoutubeClipManager.scriptInjected = true;
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(s);
  }

  /**
   * Register single callback when YT is ready; call from app once.
   */
  static onReady(callback: () => void): void {
    YoutubeClipManager.injectScript();
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      callback();
    };
  }

  /**
   * Initialize all yt-clip: iframe.yt-clip (loop start/end) and .yt-clip div (data-vid). Call once from onReady.
   */
  static initAll(): void {
    if (typeof window.YT === 'undefined') return;
    const YT = window.YT;

    // iframe.yt-clip: loop between data-start and data-end
    document.querySelectorAll<HTMLIFrameElement>('iframe.yt-clip').forEach((el) => {
      const url = el.src || '';
      if (url.indexOf('origin=') === -1) {
        el.src += (url.indexOf('?') > -1 ? '&' : '?') + 'origin=' + encodeURIComponent(location.origin);
      }
      const start = +(el.dataset.start ?? 0);
      const end = +(el.dataset.end ?? 0);
      const p = new YT.Player(el, {
        events: {
          onReady: (e: YTEvent) => {
            e.target.mute();
            e.target.setVolume(0);
            e.target.seekTo(start, true);
            e.target.playVideo();
          },
          onStateChange: (e: YTStateEvent) => {
            if (e.data === 1) {
              e.target.mute();
              e.target.setVolume(0);
              if (end > start) {
                setInterval(() => {
                  if (e.target.getCurrentTime() >= end) e.target.seekTo(start, true);
                }, 200);
              }
            }
          },
        },
      });
    });

    // .yt-clip (div): data-vid, data-start, data-end
    document.querySelectorAll('.yt-clip').forEach((el) => {
      if (el.tagName === 'IFRAME') return;
      const start = +(el.getAttribute('data-start') ?? 0);
      const end = +(el.getAttribute('data-end') ?? 0);
      const vid = el.getAttribute('data-vid') ?? '';
      const p = new YT.Player(el, {
        host: 'https://www.youtube.com',
        playerVars: { origin: location.origin, playsinline: 1, controls: 0, rel: 0, modestbranding: 1, disablekb: 1 },
        events: {
          onReady: (e: YTEvent) => {
            e.target.mute();
            e.target.loadVideoById({ videoId: vid, startSeconds: start, endSeconds: end });
            e.target.playVideo();
          },
          onStateChange: (e: YTStateEvent) => {
            if (e.data === YT.PlayerState.ENDED) {
              e.target.seekTo(start, true);
              e.target.playVideo();
            }
          },
        },
      });
      const tick = setInterval(() => {
        try {
          if (end > 0 && p.getCurrentTime() >= end - 0.15) p.seekTo(start, true);
        } catch {
          /* noop */
        }
      }, 200);
      if (!window.YT_PLAYERS) window.YT_PLAYERS = {};
      window.YT_PLAYERS[(el as HTMLElement).id || `yt-${Math.random().toString(36).slice(2)}`] = { p, tick };
    });
  }
}

interface YTGlobal {
  Player: new (el: Element, opts: object) => YTPlayer;
  PlayerState: { ENDED: number };
}
interface YTPlayer {
  mute(): void;
  setVolume(n: number): void;
  seekTo(n: number, b: boolean): void;
  playVideo(): void;
  getCurrentTime(): number;
  loadVideoById(o: { videoId: string; startSeconds?: number; endSeconds?: number }): void;
}
interface YTEvent {
  target: YTPlayer;
}
interface YTStateEvent {
  data: number;
  target: YTPlayer;
}
